import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// Libraries
import { Observable, Subject } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { concatMap, takeUntil } from 'rxjs/operators';

// Services
import { AuthService } from '@auth/auth.service';
import {
  CHANNELS_ENUMERATION,
  CHANNELS_EVENTS_ENUMERATION,
  CustomerRecurrenceService,
  CustomerRecurrenceType,
  MarketingEventService,
} from '@shared/services';
import { LoginEntryPolicy } from '@home/login/services';

// Enums
import { COOKIE_ENUMERATION } from '@core/model/enums/loan-application-state.enumeration';

// Models
import { CustomResponseDto } from '@core/model/custom-response-dto';

// Environments
import { environment } from '@environments/environment';

const LOGIN_ROUTE = '/login';
const HOME_ROUTE = '/home';

/** Grep handle for every failed SSO token handoff. Keep it stable. */
const SSO_HANDOFF_LOG_PREFIX = '[SSO_HANDOFF_FAILED]';

@Component({
  selector: 'sso',
  template: `
    <h3 id="msjErrorSso" *ngIf="error" style="padding: 30px">
      Hubo un error, te redireccionaremos para que lo intentes nuevamente
    </h3>
  `,
})
export class SsoComponent implements OnInit, OnDestroy {
  public error: boolean;
  private unSubscribe$: Subject<void>;

  constructor(
    private readonly authService: AuthService,
    private readonly customerRecurrenceService: CustomerRecurrenceService,
    private readonly activateRoute: ActivatedRoute,
    private readonly cookieService: CookieService,
    private readonly statusService: CustomerRecurrenceService,
    private readonly router: Router,
    private readonly http: HttpClient,
    private readonly marketingEventService: MarketingEventService,
    private readonly loginEntryPolicy: LoginEntryPolicy
  ) {
    this.error = false;
    this.unSubscribe$ = new Subject();
  }

  public ngOnInit(): void {
    this.startAuthenticationProcess();
  }

  public ngOnDestroy(): void {
    this.unSubscribe$.complete();
    this.unSubscribe$.unsubscribe();
  }

  private startAuthenticationProcess(): void {
    const { origin, redirect } = this.activateRoute.snapshot.queryParams;
    const accessToken: string = this.cookieService.get(COOKIE_ENUMERATION.CRFRAP);
    const tokenData = this.authService.getTokenData(accessToken);
    accessToken && tokenData
      ? this.ensureThatTheTokenIsValidAndVerifyNewOrReturningUser({ accessToken, origin, redirect })
      : this.unauthorizedDueToLackOfToken(origin, redirect);
  }

  /**
   * @description To authenticate the user,
   * it is validated that the token is valid and issued by rapicredit,
   * or for this an endpoint is assumed that executes a call in the AWS api-gateway,
   * additionally it is verified if the user is new or recurring through another of two service endpoint,
   * the logic channels are in the CustomerRecurrenceService class
   * @date 16/01/2024
   * @private
   * @param {{
   *     accessToken: string;
   *     redirect: string;
   *     origin: string;
   *   }} {
   *     accessToken,
   *     redirect,
   *     origin,
   *   }
   */
  private ensureThatTheTokenIsValidAndVerifyNewOrReturningUser({
    accessToken,
    redirect,
    origin,
  }: {
    accessToken: string;
    redirect: string;
    origin: string;
  }): void {
    this.ensureItIsAValidRapicreditToken(accessToken)
      .pipe(
        concatMap(
          (): Observable<CustomResponseDto<CustomerRecurrenceType>> =>
            this.customerRecurrenceService.getRecurrence$(accessToken)
        ),
        takeUntil(this.unSubscribe$)
      )
      .subscribe({
        next: (recurrenceResponse: CustomResponseDto<CustomerRecurrenceType>): void =>
          this.stepsToAuthTheUser({ accessToken, recurrenceResponse, redirect }),
        error: (error: HttpErrorResponse): void => this.ensureHandlingOfExceptions(error, origin, redirect),
      });
  }

  private ensureHandlingOfExceptions(error: HttpErrorResponse, origin: string, redirect: string): void {
    error.status === 401
      ? this.recoverFromFailedHandoff(redirect)
      : this.ensureHandlingOfErrorsInTheAuthProcess(origin, redirect);
  }

  private ensureHandlingOfErrorsInTheAuthProcess(origin: string, redirect: string): void {
    this.error = true;
    setTimeout((): void => this.unauthorizedDueToLackOfToken(origin, redirect), 3000);
  }

  /**
   * @description This function executes the necessary
   * logic to successfully authenticate the user and not affect other processes.
   * @date 16/01/2024
   * @private
   * @param {{
   *     accessToken: string,
   *     recurrenceResponse: CustomResponseDto<CustomerRecurrenceType>,
   *     redirect: string;
   *   }} { accessToken, recurrenceResponse, redirect }
   */
  private stepsToAuthTheUser({
    accessToken,
    recurrenceResponse,
    redirect,
  }: {
    accessToken: string;
    recurrenceResponse: CustomResponseDto<CustomerRecurrenceType>;
    redirect: string;
  }): void {
    this.authService.saveUser(accessToken);
    this.authService.saveToken(accessToken);
    this.statusService.saveRecurrenceStatus(recurrenceResponse.response);
    redirect ? this.router.navigateByUrl(redirect) : this.router.navigateByUrl(HOME_ROUTE);
  }

  private unauthorizedDueToLackOfToken(originRoute: string, redirect: string): void {
    originRoute ? this.windowRedirect(originRoute) : this.recoverFromFailedHandoff(redirect);
  }

  /**
   * The token handoff failed. Whatever happens next, the destination the caller
   * asked for has to survive this method.
   *
   * Dropping it is what silently strands users on the home screen: a bare
   * `/login` is rewritten to `/home` by {@link AuthenticatedRedirectGuard} for
   * anyone who still holds a session, so the failure never surfaces and the
   * requested screen is simply forgotten.
   */
  private recoverFromFailedHandoff(redirect: string): void {
    if (!this.isInAppDestination(redirect)) {
      this.reportFailedHandoff(redirect, 'no usable destination, falling back to login');
      this.router.navigateByUrl(LOGIN_ROUTE);
      return;
    }

    // A live session already authorises the destination — the handoff only
    // existed to deliver a token that, in this case, we turn out not to need.
    if (this.loginEntryPolicy.hasUsableSession()) {
      this.reportFailedHandoff(redirect, 'recovered with the existing session, the user sees nothing');
      this.router.navigateByUrl(redirect);
      return;
    }

    this.reportFailedHandoff(redirect, 'no session, sending the user to log in');
    this.router.navigate([LOGIN_ROUTE], { queryParams: { redirect } });
  }

  /**
   * Keeps a failed handoff visible to us even when we hide it from the user.
   *
   * Recovering through an existing session is deliberately invisible on screen,
   * which is exactly what makes it dangerous: without this line a broken token
   * handoff looks like a working flow, and the upstream defect stays buried.
   * The prefix is here to be grepped in QA consoles and, once this app has one,
   * to be picked up by a real monitoring sink.
   */
  private reportFailedHandoff(redirect: string, outcome: string): void {
    console.warn(`${SSO_HANDOFF_LOG_PREFIX} CRFRAP token unusable — ${outcome}. redirect=${redirect || '(none)'}`);
  }

  /**
   * `redirect` arrives from the query string, so it is caller-controlled. Only
   * single-slash absolute paths are honoured; `//host` and absolute URLs are
   * discarded so this cannot become a redirector to somewhere else.
   */
  private isInAppDestination(redirect: string): boolean {
    return Boolean(redirect) && redirect.startsWith('/') && !redirect.startsWith('//');
  }

  private windowRedirect(originRoute: string) {
    window.location.href = originRoute;
  }

  /**
   * @description This endpoint belongs to an aws lambda running on the api-gateway
   * @date 16/01/2024
   * @private
   * @param {string} token
   * @return {*}  {Observable<void>}
   */
  private ensureItIsAValidRapicreditToken(token: string): Observable<void> {
    return this.http.get<void>(`${environment.apiGatewayUrl}auth/validate-token`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }
}
